import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { useProductionData } from '../hooks/useProductionData';
import ProductionHeaderTailwind from '../components/production/ProductionHeaderTailwind';
import AppHeader from '../components/layout/AppHeader';
import SideFeil from '../components/common/SideFeil';
import ResourcePlanning from '../components/production/ResourcePlanning';
import PlanEditor from '../components/production/PlanEditor';
import CollapsibleStaffingTable from '../components/production/CollapsibleStaffingTable';
import OppmoteEditor from '../components/production/OppmoteEditor';
import VarslingsstatusPanel from '../components/production/VarslingsstatusPanel';
import { useAuth } from '../contexts/AuthContext';

const ProductionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const readOnly = !isAdmin();
  
  const {
    produksjon,
    bemanning,
    talentBehov,
    loading,
    error,
    ikkeFunnet,
    refetch,
    bemanningStats,
    refresh,
    refreshBemanning,
    refreshTalentBehov,
  } = useProductionData(id);
  
  const [visMedarbeidere, setVisMedarbeidere] = useState(false);
  const [visPlan, setVisPlan] = useState(false);
  const [visOppmote, setVisOppmote] = useState(false);
  // Festet bemanningsskuff (fixed, 500px) skal skyve innholdet til siden
  const [drawerPinned, setDrawerPinned] = useState(false);

  const formatDato = (tidString: string) => {
    const dato = new Date(tidString);
    return dato.toLocaleDateString('nb-NO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTid = (tidString: string) => {
    const dato = new Date(tidString);
    return dato.toLocaleTimeString('nb-NO', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Loader size={48} className="animate-spin text-blue-600" />
          <p className="text-gray-600">Laster produksjon...</p>
        </div>
      </div>
    );
  }

  // SideFeil bare når produksjonen aldri ble lastet. Feiler en senere henting (for eksempel
  // etter lagring), beholder React Query dataene, og siden står med dem.
  if (!produksjon) {
    const tilOversikten = { tekst: 'Til oversikten', til: '/dashboard' };

    // Annen feil enn 404/403 (nettverk, 500): kan gå over, så brukeren får prøve igjen
    if (error && !ikkeFunnet) {
      return (
        <SideFeil
          tittel="Kunne ikke laste produksjonen"
          tekst="Prøv igjen om litt."
          tilbake={tilOversikten}
          onProvIgjen={refetch}
        />
      );
    }

    // 404/403, eller ingen produksjon å vise (ugyldig id). Samme tekst for «finnes ikke»
    // og «annen organisasjon», så visningen ikke avslører noe.
    return (
      <SideFeil
        tittel="Fant ikke produksjonen"
        tekst="Den finnes ikke, eller du har ikke tilgang til den."
        tilbake={tilOversikten}
      />
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-50 transition-[padding-right] duration-300 ease-in-out${
        drawerPinned ? ' sm:pr-[500px]' : ''
      }`}
    >
      <AppHeader />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <ProductionHeaderTailwind
          produksjon={produksjon}
          bemanningStats={bemanningStats}
          visMedarbeidere={visMedarbeidere}
          visPlan={visPlan}
          visOppmote={visOppmote}
          onToggleMedarbeidere={() => setVisMedarbeidere(!visMedarbeidere)}
          onTogglePlan={() => setVisPlan(!visPlan)}
          onToggleOppmote={() => setVisOppmote(!visOppmote)}
          formatDato={formatDato}
          formatTid={formatTid}
          readOnly={readOnly}
        />
      </div>

      {visMedarbeidere && (
        readOnly ? (
          <div className="max-w-7xl mx-auto px-4 mb-6">
            <CollapsibleStaffingTable bemanning={bemanning} />
          </div>
        ) : (
          <ResourcePlanning
            produksjonId={parseInt(id || '0')}
            talentBehov={talentBehov as any}
            bemanning={bemanning}
            onChanged={refresh}
            refreshBemanning={refreshBemanning}
            refreshTalentBehov={refreshTalentBehov}
            readOnly={readOnly}
            onDrawerPinnedChange={setDrawerPinned}
          />
        )
      )}

      <div className="max-w-7xl mx-auto px-4">
        {visPlan && (
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <PlanEditor 
                produksjonId={parseInt(id || '0')} 
                readOnly={readOnly}
              />
            </div>
          </div>
        )}

        {visOppmote && (
          <div className="mb-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <OppmoteEditor
                produksjonId={parseInt(id || '0')}
                produksjonTid={produksjon.tid}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}

        {!readOnly && (
          <div className="mb-6">
            <VarslingsstatusPanel produksjonId={parseInt(id || '0')} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionDetail;
